<template>
  <div v-if="type == 'assets'">
    <v-row>
      <v-col cols="3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          single-line
          hide-details
        ></v-text-field>
      </v-col>

      <TableRefresh 
				class="pt-1"
        :state="loading"
        @click.native="refreshTable"
      />

      <v-spacer/>
			<v-col cols="1">
				<v-icon v-if="!excelLoading" @click="exportExcel">
					mdi-file
				</v-icon>
				<v-icon v-else class="updating">
					mdi-refresh
				</v-icon>
			</v-col>
    </v-row>

    <div class="assets-page-container">
      <v-data-table
        v-model="selectedAssets"
        :headers="headers"
        :items="tableData"
        :loading="loading"
        :search="search"
        show-select
        item-key="id"
        class="elevation-1 admin-table hardware-faults-report"
      >
       
        <template v-slot:item.serial="{ item }">
          {{ getDeviceInfo(item, 'serial_number') }}
        </template>

        <template v-slot:item.imei="{ item }">
          {{ getDeviceInfo(item, 'imei') }}
        </template>

        <template v-slot:item.last_seen="{ item }">
          {{ formatDate(readValueFromPresenceMap(item, "last_seen")) }}
        </template>

        <template v-slot:item.location="{ item }">
          {{ readValueFromPresenceMap(item, "location") }}
        </template>

        <template v-slot:item.battery_level="{ item }">
          {{ readValueFromPresenceMap(item, "battery_level") }}
        </template>

        <template v-slot:item.notes="{ item }">
          <v-layout justify-center>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on">
                  mdi-file
                </v-icon>
              </template>
              <div class="asset-notes">
                <h6 class="notes-title">Notes: {{ item.vehicle_name }}</h6>
                <div v-if="item.notes != ''" class="notes-body">
                  {{ item.notes }}
                </div>
                <div v-else class="notes-body">
                  N/A
                </div>
              </div>
            </v-tooltip>
          </v-layout>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="reports-action-launch" @click="launchRowAction">
            <v-icon>mdi-dots-vertical</v-icon>
            <ActionContainer
              :ref="item.key"
              :heading="'Action'"
              class="report-action-options"
            >
              <ActionItem
                
                label="Edit asset"
                icon="mdi-pencil"
                :disabled="allowOnlySingleAsset || !$can('updateAsset')"
                @click.native="showDialogAddCurrent(item, 'edit')"
              />

              <ActionItem
                
                :disabled="allowOnlySingleAsset || !$can('viewAsset')"
                label="Device Information"
                icon="mdi-chip"
                @click.native="showDialogAddCurrent(item, 'device_info')"
              />

              <ActionItem
                
                label="Move to Org"
                :disabled="!$can('moveAsset')"
                icon="mdi-arrow-right"
                @click.native="showDialogAddCurrent(item, 'move')"
              />
              <ActionItem
                
                :disabled="allowOnlySingleAsset || !$can('viewAsset')"
                label="Show Location"
                icon="mdi-map-marker"
                @click.native="showDialogAddCurrent(item, 'location')"
              />
              <ActionItem
                
                label="Delete Asset"
                :disabled="!$can('disableAsset')"
                icon="mdi-delete"
                @click.native="showDialogAddCurrent(item, 'delete')"
              />
            </ActionContainer>
          </div>
        </template>
      </v-data-table>

      <Pagination
        :pagination-info="paginationInfo"
        @updatedPage="fetchNewPage"
      />
    </div>

    <Dialog
      v-if="showEditAsset && selectedAssets.length > 0"
      :display="showEditAsset"
      :title="'Edit Asset'"
      :state="false"
      :res="{}"
      @close="toggleEditAsset"
    >
      <v-row class="pb-0">
        <v-col cols="4" class="pb-0 pt-0">
          Name
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-text-field v-model="editAssetPayloadObj.name" />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Type
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-select
            v-model="editAssetPayloadObj.asset_type"
            :items="assetTypes"
          />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Make
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-text-field v-model="editAssetPayloadObj.make" />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Model
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-text-field v-model="editAssetPayloadObj.model" />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Year
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-text-field v-model="editAssetPayloadObj.year" />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Group
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-select
            v-model="editAssetPayloadObj.node_id"
            :items="vehicleGroups"
            item-value="id"
            item-text="name"
          />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-col cols="4" class="pb-0 pt-0">
          Notes
        </v-col>
        <v-col cols="8" class="pb-0 pt-0">
          <v-textarea v-model="editAssetPayloadObj.notes" />
        </v-col>
      </v-row>

      <v-row class="pb-0 pt-0">
        <v-spacer></v-spacer>
        <v-col cols="2">
          <v-btn text color="primary" @click="toggleEditAsset">cancel</v-btn>
        </v-col>
        <v-col cols="2">
          <v-btn
            text
            color="primary"
            :loading="updatingAsset"
            @click.native="updateOrgAssets"
            >Submit</v-btn
          >
        </v-col>
      </v-row>
    </Dialog>

    <Dialog
      v-if="firstAssetInSelection"
      :display="showDeviceInfo"
      :title="'Device Information'"
      :state="false"
      :res="{}"
      @close="toggleDeviceInfo"
    >
      <v-row class="pb-1 pt-1 grey-bg">
        <v-col cols="6" class="pb-0 pt-0">
          Serial Number
        </v-col>
        <v-col cols="6" class="pb-0 pt-0 device-label">
          {{ firstAssetInSelection.serial_number }}
        </v-col>
      </v-row>

      <v-row class="pb-1 pt-1">
        <v-col cols="6" class="pb-0 pt-0">
          IMEI
        </v-col>
        <v-col cols="6" class="pb-0 pt-0 device-label">
          {{ firstAssetInSelection.imei }}
        </v-col>
      </v-row>

      <v-row class="pb-1 pt-1 grey-bg">
        <v-col cols="6" class="pb-0 pt-0">
          SIM ICCID
        </v-col>
        <v-col cols="6" class="pb-0 pt-0 device-label">
          {{ firstAssetInSelection.iccid }}
        </v-col>
      </v-row>

      <v-row class="pb-1 pt-1">
        <v-col cols="6" class="pb-0 pt-0">
          Model
        </v-col>
        <v-col cols="6" class="pb-0 pt-0 device-label">
          {{ firstAssetInSelection.device_model }}
        </v-col>
      </v-row>

      <v-row class="pb-1 pt-1 grey-bg">
        <v-col cols="6" class="pb-0 pt-0">
          Firmware Version
        </v-col>
        <v-col cols="6" class="pb-0 pt-0 device-label">
          {{ firstAssetInSelection.firmware_version }}
        </v-col>
      </v-row>
    </Dialog>

    <MoveAssetsBetweenOrgs
      v-if="displayMoveAsset"
      :orgs="orgs"
      :asset-list="selectedAssets"
      @close="displayMoveAsset = false"
    />

    <Dialog
      :display="showDeleteAsset"
      :title="'Delete Asset'"
      :state="false"
      :res="{}"
      @close="showDeleteAsset = false"
    >
      <v-row>
        <v-col cols="3">
          <v-icon large>
            mdi-alert
          </v-icon>
        </v-col>
        <v-col v-if="selectedAssets.length == 1" cols="9">
          The asset will be deleted and its associated device returned to the
          unassigned device pool. Are you sure you want to proceed?
        </v-col>
        <v-col v-else cols="9">
          {{ selectedAssets.length }} assets will be deleted and their
          associated device returned to the unassigned device pool. Are you sure
          you want to proceed?
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="2">
          <v-btn text @click="showDeleteAsset = false">
            cancel
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col cols="2">
          <v-btn text :loading="deletingAsset" @click="deleteAsset">
            yes
          </v-btn>
        </v-col>
      </v-row>
    </Dialog>

    <Dialog
      :display="showAssetLocation"
      :title="'Asset Location'"
      :state="false"
      :res="{}"
      @close="showAssetLocation = false"
    >
      <div v-if="assetLocation && showAssetLocation" class="asset-map">
        <pvcam-wc-map-simple v-if="assetLocation && showAssetLocation" width="100%" height="100%"></pvcam-wc-map-simple>
      </div>
    </Dialog>
  </div>
</template>

<script>

import { mapState, mapMutations } from "vuex";

import Dialog from "@/components/general/Dialog.vue";
import MoveAssetsBetweenOrgs from "@/components/general/MoveAssetsBetweenOrgs";
import ActionContainer from "@/components/general/ActionContainer.vue";
import ActionItem from "@/components/general/ActionItem.vue";
import Pagination from './Pagination.vue';
import TableRefresh from '@/components/general/TableRefresh.vue';

import {
  GetOrgTree,
  getOrgAssets,
  getOrgAssetsPresense,
  updateOrgAssets,
  deleteAssets
} from "@/services/API.js";
import { getCoor } from "@/utils";
import { search } from '@/mixins/quickSearch.js'

const { CameraMatics } = window;

import moment from "moment";
import * as xlsx from "xlsx";

import { Logger } from "aws-amplify";
const logger = new Logger("Asset Search Results");

export default {
  name: 'Assets',

  components: {
    Dialog,
    MoveAssetsBetweenOrgs,
    ActionContainer,
    ActionItem,
    Pagination,
    TableRefresh
  },

  mixins: [ search ],

  props: {
    update: {
      type: Number,
      default: 0
    },
    type: {
      required: true,
      type: String
    }
  },

  data() {
    return {
      selectedOrg: "",
      selectedGroup: "",
      nodes: [],
      groups: [],
      assets: [],
      assetPresence: {},
      selectedAssets: [],
      loading: false,
      search: "",
      page: 1,
      totalPages: 1,
      itemsPerPage: 50,

      headers: [
        {
          text: "Organisation",
          value: "org",
          align: "left",
        },
        {
          text: "Group",
          value: "node_name",
          align: "left",
        },
        {
          text: "Name",
          value: "name",
          align: "left",
        },
        {
          text: "Type",
          value: "asset_type",
          align: "left",
        },
        {
          text: "Asset Make",
          value: "make",
          align: "left",
        },
        {
          text: "Asset Model",
          value: "model",
          align: "left",
        },
        {
          text: "Year",
          value: "year",
          align: "left",
        },
        {
          text: "Serial",
          value: "serial",
          align: "left",
        },
        {
          text: "IMEI",
          value: "imei",
          align: "left",
        },
        {
          text: "Last Seen",
          value: "last_seen",
          align: "left",
        },
        {
          text: "Battery (mv)",
          value: "battery_level",
          align: "left",
        },
        {
          text: "Notes",
          value: "notes",
          align: "left",
        }
      ],

      editAssetPayloadObj: {
        name: "",
        asset_id: "",
        asset_type: "",
        make: "",
        model: "",
        year: "",
        node_id: "",
        notes: "",
      },
      editAssetPayloadList: [],
      showEditAsset: false,
      updatingAsset: false,
      assetTypes: ["Trailer"],

      showDeviceInfo: false,

      displayMoveAsset: false,

      showDeleteAsset: false,
      deletingAsset: false,

      showAssetLocation: false,

      tableData: [],
      orgIds: [],
      presenceData: [],

      paginationInfo: {
        pageNum: 1,
        pageCount: 1,
        pageSize: 100
      },
    };
  },

  computed: {
    ...mapState([
      'quickSearchResults',
      'quickSearchPayload',
      'orgs'
    ]),

    assetSearchResults() {
      if (this.selectedGroup == this.groups[0]) return this.assets;
      return this.assets.filter(
        (asset) => asset.node_name == this.selectedGroup
      );
    },

    firstAssetInSelection() {
      return this.selectedAssets.length > 0
        ? this.selectedAssets[0].devices[0]
        : null;
    },

    lastSelectedAsset() {
      if (!this.selectedAssets) return;
      const total = this.selectedAssets.length;

      return this.selectedAssets.length > 0
        ? this.selectedAssets[total - 1]
        : null;
    },

    assetLocation() {
      if (!this.lastSelectedAsset) return;
      if (this.lastSelectedAsset.length <= 0 || !this.lastSelectedAsset)
        return false;

      if (Object.hasOwn(this.assetPresence, this.lastSelectedAsset.id)) {
        return this.assetPresence[this.lastSelectedAsset.asset_id]["coords"];
      } else {
        return false;
      }
    },

    allowOnlySingleAsset() {
      return this.selectedAssets.length > 0 ? true : false;
    },

    selectedAssetsIds() {
      return this.selectedAssets.map((asset) => asset.id);
    },

    hasData() {
      const data = this.quickSearchResults;

      return Object.keys(data).length > 0;
    },

    hasPayload() {
      const data = this.quickSearchPayload;

      return Object.keys(data).length > 0;
    }
  },

  watch: {
    quickSearchResults: {
      handler() {
        logger.info('quick search res')
        this.compileTableData();
        this.buildPaginationInfo();
      },
      deep: true
    },

    showAssetLocation(val) {
      if(val) {
        setTimeout(() => {
          this.showLocation(this.assetLocation)
        }, 750)
      }
    }
  },

  mounted() {
    this.compileTableData();
    this.buildPaginationInfo();

    window.$translate = (section, value) => {
      logger.info('translate', section, value)
    }
  },

  methods: {
    ...mapMutations("presence", [
      "setOrgToWatch"
    ]),

    async refreshTable() {
      if(!this.hasPayload) return;
      this.loading = true;
      this.tableData = []
      const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

      if (search) {
        this.compileTableData();
        this.buildPaginationInfo();
        this.loading = false;
      }
      else {
        this.loading = false;
      }

    },

    getDeviceInfo(item, attribute) {
      if(!item || !attribute) return
      if(!Object.hasOwn(item, 'devices')) return
      
      let device = item.devices.find(device => device.is_default_device == 'true' || device.is_default_device == true)

      return device ? device[attribute] : ''
    },

    showLocation (location) {

      if(location) {
        try {
          window.EventBus.$emit("showEvent", {
            coor: location,
            icon: 'trailer',
            content: false
          })
        } 
        
        catch (error) {
            logger.debug("event error", error)
        }
      }
    },

    async fetchNewPage(page) {
        if(!this.hasData) return
        if(page == this.currentPage) return

        this.loading = true;

        this.$store.commit('updateQuickSearchPage', page);

        const search = await this.quickSearch(this.quickSearchPayload, this.currentRoute);

        if (search) {
          this.compileTableData();
          this.buildPaginationInfo();
          this.$store.commit('setQuickSearchPayload', this.quickSearchPayload);
          this.loading = false;
        }
        else {
          this.loading = false;
        }
        logger.info('handle page update ', page)
      },

    buildPaginationInfo() {
      logger.info('buildPaginationInfo')
      if(!this.quickSearchResults.hasOwnProperty('res')) return;

      const data = this.quickSearchResults.res;

      if(data.hasOwnProperty('page_number')) {
        this.paginationInfo.pageNum = data.page_number;
      }
      if(data.hasOwnProperty('total_pages')) {
        this.paginationInfo.pageCount = data.total_pages;
      }
      logger.info('has pagination info ', this.paginationInfo)
    },

    compileTableData() {
      const data = this.quickSearchResults;
      let results = []

      if(this.hasData) {
        if (data.res.Result.length > 0) {
          results = JSON.parse(JSON.stringify(data.res.Result))
        } else {
          results = [];
        }
        
        this.tableData = results.map((asset) => {
          return asset;
        });

        if (this.tableData.length > 0) {
          this.compileOrgIds();
        }
      }
      logger.info('table data',this.tableData)
    },

    compileOrgIds() {
      logger.info('compileOrgIds called')
      this.orgIds = [];
    
      this.tableData.forEach(vehi => {
        let id = vehi.org_id;

        if (!this.orgIds.includes(id) && id.length > 0) {
          logger.info('what is org id', id)
          this.orgIds.push(id);
        }  
      });

      this.getPresenceData();
    },

    async getPresenceData() {
      let collection = [];
      for (let i = 0; i < this.orgIds.length; i++) {
        collection.push(this.getOrgAssetsPresense(this.orgIds[i]));
      }		
      
      const results = await Promise.all(collection);
      logger.info('what is results?', results)
      const combinedResults = [].concat.apply([], results);

      this.presenceData = combinedResults;
    },

    async deleteAsset() {
      this.deletingAsset = true;

      try {
        const deleteAsset = await deleteAssets(
          this.orgId,
          this.selectedAssetsIds
        );

        if (
          deleteAsset &&
          Object.hasOwn(deleteAsset, "results") &&
          deleteAsset.results.length > 0
        ) {
          this.deletingAsset = false;
          this.getAssetsByOrg(true);
        } else {
          throw new Error("A problem occured deleting the asset(s)");
        }
      } catch {
        this.deletingAsset = false;
      }
    },

    async updateOrgAssets() {
      this.updatingAsset = true;
      try {
        const update = await updateOrgAssets(this.editAssetPayloadList);

        if (update && Object.hasOwn(update, "assets")) {
          this.updatingAsset = false;
          this.getAssetsByOrg(true);
        } else {
          throw new Error("A problem occured updating the asset(s)");
        }
      } catch {
        this.updatingAsset = false;
      }
    },

    async fetchOrgNodes() {
      this.selectedGroup = "";
      const orgTree = await GetOrgTree(this.orgId);

      if (orgTree) {
        this.nodes = orgTree;

        this.groups = this.flattenOrgTree([this.nodes.root_node]);
        this.selectedGroup = this.groups[0];
      }
    },

    retrunOrgID(name) {
      let org = this.orgs.find((org) => {
        return org.name == name;
      });

      return org ? org.id : "";
    },

    flattenOrgTree(node) {
      var result = [];

      node.forEach((a) => {
        result.push(a.metadata.label);
        if (Array.isArray(a.nodes)) {
          result = result.concat(this.flattenOrgTree(a.nodes));
        }
      });
      return result;
    },

    async getAssetsByOrg(refresh = false) {
      this.loading = true;

      if (refresh) {
        this.assets = [];
      }

      await Promise.all([this.getOrgAssetsPresense(), this.getOrgAssets()])
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    async getOrgAssetsPresense(id) {
      const presence = await getOrgAssetsPresense(id);
      const addLocations = await this.findLocations(presence);

      return addLocations
    },

    async getOrgAssets() {
      const assets = await getOrgAssets(
        this.orgId,
        this.page,
        this.itemsPerPage
      );

      if (assets && Object.hasOwn(assets, "Assets")) {
        logger.info("asset devices? ", assets.Assets);
        this.assets = assets.Assets;
        this.totalPages = assets.page_size;
      }
    },

    readValueFromPresenceMap(asset, val) {
      if (!asset || !val) return "";

      const data = this.presenceData.find(item => item.asset_id == asset.id)

      if(data) {
        return data[val]
      }
   
    },

    formatDate(val) {
      if (!val) return "";
      return moment(val).format("DD MMM YYYY HH:mm:ss");
    },

    findLocations(dataSet, propetyName = "location") {
      return new Promise(async (res, rej) => {
        try {
          let coords = dataSet.map((i, index) => {
            i.arrayPos = index;
            i.coords = {
              lat: getCoor(i.last_location.coords.lat),
              lng: getCoor(i.last_location.coords.long),
            };
            return i;
          });

          // we want no run all of these requests async so we map each request to a promise
          const promises = coords.map(
            (i) =>
              new Promise(async (res) => {
                try {
                  const location = await CameraMatics.HereMapService.multiRevGeocode(
                    [i.coords]
                  );
                  // if we can find the location
                  if (location.Response) {
                    coords[i.arrayPos][propetyName] =
                      location.Response.Item[0].Result[0].Location.Address.Label;
                    res(true);
                    // if we can't find the location
                  } else {
                    coords[i.arrayPos][propetyName] = "Unknown";
                    res(true);
                  }
                  // if something goes wrong, we just set the location as not known!
                } catch (exception) {
                  coords[i.arrayPos][propetyName] = "Unknown";
                  res(true);
                }
              })
          );

          // wait for all requests to finish the return the ammended set
          await Promise.all(promises);
          res(coords);
        } catch (ex) {
          logger.debug("error finding locations", ex);
          rej(false);
        }
      });
    },

    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove("active-options");
        if (option === e.target.nextSibling) {
          option.classList.toggle("active-options");
        }
      }
    },

    showDialogAddCurrent(item, type) {
      let selectedAmount = this.selectedAssets.length;

      if (item) {
        if (!this.selectedAssets.includes(item)) {
          this.selectedAssets.push(item);
        }
      } else {
        // Clear selection for single select

        if (selectedAmount === 1) {
          this.selectedAssets = [];
        }
      }

      if (type == "edit") {
        this.toggleEditAsset();
      }

      if (type == "device_info") {
        this.toggleDeviceInfo();
      }

      if (type == "move") {
        this.displayMoveAsset = true;
      }
      if (type == "unassign") {
        this.$emit("unassignAssetDevice");
      }
      if (type == "location") {
        this.showAssetLocation = true;
      }
      if (type == "delete") {
        this.showDeleteAsset = true;
      }
    },

    toggleEditAsset() {
      this.showUpdateError = false;
      this.editAssetPayloadList = [];
      let masterAsset = {};

      if (this.selectedAssets.length > 0) {
        masterAsset = this.selectedAssets[0];

        this.editAssetPayloadObj.name = masterAsset.name;
        this.editAssetPayloadObj.asset_id = masterAsset.id;
        this.editAssetPayloadObj.asset_type = masterAsset.asset_type;
        this.editAssetPayloadObj.make = masterAsset.make;
        this.editAssetPayloadObj.model = masterAsset.model;
        this.editAssetPayloadObj.year = masterAsset.year;
        this.editAssetPayloadObj.node_id = masterAsset.node;
        this.editAssetPayloadObj.notes = masterAsset.notes;
      }

      for (const asset in this.selectedAssets) {
        logger.info("asset?", this.selectedAssets[asset]);

        this.editAssetPayloadList.push({
          name: masterAsset.asset_name,
          asset_id: this.selectedAssets[asset].id,
          asset_type: masterAsset.asset_type,
          make: masterAsset.make,
          model: masterAsset.model,
          year: masterAsset.year,
          node_id: masterAsset.node,
          notes: masterAsset.notes,
        });
      }

      this.showEditAsset = !this.showEditAsset;
    },

    toggleDeviceInfo() {
      this.showDeviceInfo = !this.showDeviceInfo;
    },

    async getAllOrgAssets() {
      const allResults = [];
      let currentPage = 1;

      for (let i = 0; i < this.totalPages; i++) {
        try {
          currentPage = i + 1;

          const assets = await getOrgAssets(
            this.orgId,
            currentPage,
            this.itemsPerPage
          );

          if (!assets) {
            throw new Error(`Request failed`);
          }
          const data = await assets.Assets;

          if (Array.isArray(data)) {
            allResults.push(...data);
          } else {
            throw new Error(`Result is not array ${assets.Assets}`);
          }
        } catch (error) {
          throw new Error(`Error during API call ${i + 1}:`, error);
        }
      }

      return allResults;
    },

    async exportExcel() {
      this.excelLoading = true;
      const exportSheetName = `assets_${moment().format(
        "YYYY-MM-DDTHH:mm:ss"
      )}`;

      const headers = [
        "Organisation",
        "Group",
        "Name",
        "Type",
        "Asset Make",
        "Asset Model",
        "Year",
        "Serial",
        "IMEI",
        "Last Seen",
        "Battery Level (mv)"
      ];

      const payloadCopy = JSON.parse(JSON.stringify(this.quickSearchPayload))
      const fullSearch = await this.fullSearch(payloadCopy, this.paginationInfo)

      const assets = fullSearch.map((item) => ({
        ...item,
        last_seen: this.readValueFromPresenceMap(item, "last_seen"),
        location: this.readValueFromPresenceMap(item, "location"),
        battery_level: this.readValueFromPresenceMap(item, "battery_level"),
        serial: this.getDeviceInfo(item, 'serial_number'),
        imei: this.getDeviceInfo(item, 'imei')
      }));

      const data = [
        headers,
        [],
        ...assets.map((item) => [
          item.org,
          item.node_name,
          item.name,
          item.asset_type,
          item.make,
          item.model,
          item.year,
          item.serial,
          item.imei,
          item.last_seen,
          item.battery_level
        ]),
      ];

      // Create a new workbook
      const wb = xlsx.utils.book_new();

      const ws = xlsx.utils.aoa_to_sheet(data);

      const colWidths = data[0].map((_, colIndex) => {
        const maxLength = data.reduce((max, row) => {
          const cellValue =
            row && row[colIndex] ? row[colIndex].toString() : "";
          return Math.max(max, cellValue.length);
        }, 0);
        return { width: maxLength + 2 }; // Add a little extra padding
      });

      // Set column widths in the worksheet
      ws["!cols"] = colWidths;

      // Append the worksheet to the workbook
      xlsx.utils.book_append_sheet(wb, ws, "Assets");

      // Write the workbook to a file (e.g., 'example.xlsx')
      xlsx.writeFile(wb, exportSheetName + ".xlsx");
      this.excelLoading = false;
    },
  }
}

</script>